import { Box, Button, Divider, Table, TableBody, TableHead, TableRow, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Notify from "config/notify";
import useLoadMore from "hooks/useLoadMore";
import TableRowComponent from "components/table/TableRowComponent";
import TableCellComponent from "components/table/TableCellComponent";
import useCsv from "../hook/useCsv";
import FileUploadInput from "../components/FileUploadInput";

/**
 * CsvList Component
 *
 * Provides an interface for uploading various CSV master files and displays the upload history.
 * Handles file selection, triggers uploads on file change, and shows upload results in a table.
 *
 */
function CsvList() {
  const navigate = useNavigate();
  const [tireDetail, setTireDetail] = useState<File | null>(null);
  const [wheelDetail, setWheelDetail] = useState<File | null>(null);
  const [tireSpecialPrice, setTireSpecialPrice] = useState<File | null>(null);
  const [cashback, setCashback] = useState<File | null>(null);
  const [cisp, setCisp] = useState<File | null>(null);

  const { csvList, csvDownload, handleUploadCsv, getCsv, loading, onLoadMore } = useCsv();
  const { renderNodeLoader } = useLoadMore(onLoadMore, loading);

  const uploadCsv = async (type: string) => {
    try {
      let status;
      switch (type) {
        case "mst_item_detail_tire":
          if (tireDetail) {
            const response = await handleUploadCsv(tireDetail, type);
            status = response?.status;
          }
          break;
        case "mst_item_detail_wheel":
          if (wheelDetail) {
            const response = await handleUploadCsv(wheelDetail, type);
            status = response?.status;
          }
          break;
        case "mst_4bundle_price_twset":
          if (tireSpecialPrice) {
            const response = await handleUploadCsv(tireSpecialPrice, type);
            status = response?.status;
          }
          break;
        case "mst_item_cashback":
          if (cashback) {
            const response = await handleUploadCsv(cashback, type);
            status = response?.status;
          }
          break;
        case "mst_cisp":
          if (cisp) {
            const response = await handleUploadCsv(cisp, type);
            status = response?.status;
          }
          break;
        default:
          break;
      }
      if (status === 200) {
        Notify.setNotification({
          show: true,
          title: "読み込みが完了しました",
        });
      }
    } catch (e) {
      console.error("Error uploading CSV:", e);
    }
  };

  // Handle file changes
  const handleFileChange = (fileType: string) => (file: File | null) => {
    switch (fileType) {
      case "mst_item_detail_tire":
        setTireDetail(file);
        break;
      case "mst_item_detail_wheel":
        setWheelDetail(file);
        break;
      case "mst_4bundle_price_twset":
        setTireSpecialPrice(file);
        break;
      case "mst_item_cashback":
        setCashback(file);
        break;
      case "mst_cisp":
        setCisp(file);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (tireDetail) {
      uploadCsv("mst_item_detail_tire");
    }
  }, [tireDetail]);
  useEffect(() => {
    if (wheelDetail) {
      uploadCsv("mst_item_detail_wheel");
    }
  }, [wheelDetail]);
  useEffect(() => {
    if (tireSpecialPrice) {
      uploadCsv("mst_4bundle_price_twset");
    }
  }, [tireSpecialPrice]);
  useEffect(() => {
    if (cashback) {
      uploadCsv("mst_item_cashback");
    }
  }, [cashback]);
  useEffect(() => {
    if (cisp) {
      uploadCsv("mst_cisp");
    }
  }, [cisp]);

  return (
    <>
      <Box sx={{ px: { xs: 2, md: 2.5, lg: 5 }, pt: { xs: 2, md: 2.5, lg: 3 } }}>
        <Button onClick={() => navigate(-1)} variant="custom-outlined">
          <Box>＜ 戻る</Box>
        </Button>
      </Box>
      <Box
        sx={{
          p: { xs: 2, md: 2.5, lg: 5 },
          flexWrap: { xs: "wrap", md: "nowrap" },
          display: { sm: "block", xl: "flex" },
          columnGap: "153px",
        }}
      >
      <Box
        sx={{
          textAlign: "right",
          display: "inline-block",
          flexBasis: { xs: "100%", md: "auto" },
          marginBottom: 3,
        }}
      >
        <Typography
          margin={0}
          textAlign="right"
          fontSize={{ xs: 24, md: 28, lg: 23 }}
          whiteSpace="nowrap"
          color="var(--gray-700)"
        >
          マスタ管理
        </Typography>
        <Typography
          margin={0}
          textAlign="right"
          fontSize={{ xs: 20, md: 28 }}
          color="var(--gray-700)"
        >
          CSVアップロード
        </Typography>
      </Box>
      <Box
        flexGrow="1"
        display="flex"
        flexDirection="column"
        alignItems={{ xs: "start", xl: "start" }}
      >
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="start"
          flex={1}
          sx={{ width: { xs: "100%" } }}
        >
          <Box
            sx={{ fontSize: { xs: "24px", md: "33px" } }}
            fontWeight="medium"
            color="var(--gray-700)"
            whiteSpace="nowrap"
            marginBottom="27px"
          >
            各種CSV アップロード
          </Box>
          <Box
            display="flex"
            alignItems="end"
            marginBottom="27px"
            flexWrap="wrap"
            columnGap="116px"
          >
            <Box
              sx={{ fontSize: { xs: 16, md: 18, lg: 20 } }}
              fontWeight="medium"
              color="var(--gray-700)"
            >
              使い方：最終版を全件ダウンロードし、編集を行い、全件アップロードをお願いします
            </Box>
            <Box
              fontSize={{ xs: 16, md: 18, lg: 20 }}
              fontWeight="regular"
              color="var(--black)"
              whiteSpace="nowrap"
            >
              ※上記をクリックして最終版を全件ダウンロードできます
            </Box>
          </Box>

          <FileUploadInput
            label="タイヤ詳細マスタ"
            onFileChange={handleFileChange("mst_item_detail_tire")}
            sx={{ marginBottom: "42px" }}
            latestDate={csvDownload?.mst_item_detail_tire_latest_date}
            url={csvDownload?.mst_item_detail_tire_url}
            fileAccept="text/csv"
          />
          <FileUploadInput
            label="ホイール詳細マスタ"
            onFileChange={handleFileChange("mst_item_detail_wheel")}
            sx={{ marginBottom: "42px" }}
            latestDate={csvDownload?.mst_item_detail_wheel_latest_date}
            url={csvDownload?.mst_item_detail_wheel_url}
            fileAccept="text/csv"
          />
          <FileUploadInput
            label="タイヤ特価ファイル"
            onFileChange={handleFileChange("mst_4bundle_price_twset")}
            sx={{ marginBottom: "42px" }}
            latestDate={csvDownload?.mst_4bundle_price_twset_latest_date}
            url={csvDownload?.mst_4bundle_price_twset_url}
            fileAccept="text/csv"
          />
          <FileUploadInput
            label="キャッシュバック"
            onFileChange={handleFileChange("mst_item_cashback")}
            sx={{ marginBottom: "42px" }}
            latestDate={csvDownload?.mst_item_cashback_latest_date}
            url={csvDownload?.mst_item_cashback_url}
            fileAccept="text/csv"
          />
          <FileUploadInput
            label="タイヤホイール組合せ特価"
            onFileChange={handleFileChange("mst_cisp")}
            sx={{ marginBottom: "42px" }}
            latestDate={csvDownload?.mst_cisp_latest_date}
            url={csvDownload?.mst_cisp_url}
            fileAccept="text/csv"
          />
        </Box>
        <Divider
          sx={{
            width: "100%",
            marginBottom: 4,
            marginTop: 0,
            borderBottom: "1px solid var(--gray-250)",
          }}
        />
        <Box
          sx={{ fontSize: { xs: "24px", md: "33px" } }}
          fontWeight="medium"
          color="var(--gray-700)"
          whiteSpace="nowrap"
          marginBottom="27px"
        >
          アップロード履歴
        </Box>
        <Box
          sx={{
            overflowX: "auto",
            width: { xs: "100%", lg: "auto" },
            display: "flex",
            justifyContent: "left",
          }}
        >
          <Table sx={{ minWidth: 830, width: { xs: "100%", lg: "auto" }, boxShadow: "none" }}>
            <TableHead>
              <TableRowComponent
                data={[
                  { label: "ファイル名", sx: { width: 400 } },
                  { label: "日時", sx: { width: 300 } },
                  { label: "結果", sx: { width: 125 } },
                ]}
                type="header"
              />
            </TableHead>
            <TableBody>
              {csvList.map((csv, index) => (
                <TableRow key={csv.id} className={index % 2 === 0 ? "even" : "odd"}>
                  <TableCellComponent>{csv.csv_filename}</TableCellComponent>
                  <TableCellComponent>{csv.csv_datetime}</TableCellComponent>
                  <TableCellComponent>{csv.link_result ? "成功" : "NG"}</TableCellComponent>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {renderNodeLoader()}
        </Box>
      </Box>
    </Box>
    </>
  );
}

export default CsvList;
